import html2Element from "@/utils/html2Element.js";
import noScroll from "@/utils/noScroll.js";

// Стек для управления отображением всплывающих окон
const popupsStack = [];
// Текущее отображаемое всплывающее окно
let currentPopup = null;

/**
 * Отображает всплывающее окно с переданным контентом и параметрами.
 * @param {string|HTMLElement} content - Контент всплывающего окна (HTML-код или DOM-элемент).
 * @param {Object} options - Дополнительные параметры для настройки отображения всплывающего окна.
 * @returns {Object} - Объект управления всплывающим окном.
 */
export default function showPopup(content, options) {
    // Создаем объект для управления всплывающим окном
    const control = {};
    
    // Добавляем всплывающее окно в стек
    popupsStack.push({
        content: content,
        options: options || {},
        control: control
    });

    // Обновляем стек отображаемых всплывающих окон
    updateStack();

    // Возвращаем объект управления всплывающим окном
    return control;
}

/**
 * Обновляет стек отображаемых всплывающих окон.
 */
function updateStack() {
    // Если уже отображается всплывающее окно или стек пуст, выходим из функции
    if (currentPopup || popupsStack.length < 1) {
        return;
    }

    // Получаем данные текущего всплывающего окна из стека
    currentPopup = popupsStack.shift();

    // Извлекаем контент и параметры
    const content = currentPopup.content;
    const options = currentPopup.options;

    // HTML-шаблон для всплывающего окна (можно задать через параметры)
    const template = options.template || `
        <div class="popup-overlay">
            <div class="popup-window">
                <button type="button" class="popup-close-button">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                          d="M20 21.4744L11.2628 30.2115C11.062 30.4124 10.8189 30.5155 10.5337 30.5208C10.2484 30.5262 10.0001 30.4231 9.78852 30.2115C9.57697 30 9.47119 29.7543 9.47119 29.4744C9.47119 29.1945 9.57697 28.9488 9.78852 28.7372L18.5257 20L9.78852 11.2628C9.58766 11.062 9.48457 10.8189 9.47923 10.5337C9.47387 10.2484 9.57697 10.0001 9.78852 9.78853C10.0001 9.57697 10.2458 9.47119 10.5257 9.47119C10.8056 9.47119 11.0513 9.57697 11.2628 9.78853L20 18.5257L28.7372 9.78853C28.9381 9.58766 29.1811 9.48457 29.4664 9.47923C29.7516 9.47387 30 9.57697 30.2115 9.78853C30.4231 10.0001 30.5289 10.2458 30.5289 10.5257C30.5289 10.8056 30.4231 11.0513 30.2115 11.2628L21.4744 20L30.2115 28.7372C30.4124 28.9381 30.5155 29.1811 30.5208 29.4664C30.5262 29.7516 30.4231 30 30.2115 30.2115C30 30.4231 29.7543 30.5289 29.4744 30.5289C29.1945 30.5289 28.9488 30.4231 28.7372 30.2115L20 21.4744Z"
                          fill="currentColor" />
                  </svg>
                </button>
                <div class="popup-content"></div>
            </div>
        </div>
    `;

    // Создаем DOM-элемент на основе HTML-шаблона
    let popupElement = html2Element(template);
    const popupWindow = popupElement.querySelector('.popup-window');
    const popupCloseButton = popupElement.querySelector('.popup-close-button');
    const popupContent = popupElement.querySelector('.popup-content');
    
    // Добавляем пользовательский класс к корневому элементу, если указан в параметрах
    if (options.rootClass) {
        if (Array.isArray(options.rootClass)) {
            options.rootClass.forEach(c => popupElement.classList.add(c));
        } else if (typeof options.rootClass === 'string' || options.rootClass instanceof String) {
            popupElement.classList.add(options.rootClass);
        }
    }

    // Заполняем содержимое всплывающего окна
    if (typeof content === 'string' || content instanceof String) {
        popupContent.innerHTML = content;
    } else if (content instanceof HTMLElement) {
        popupContent.appendChild(content);
    } else {
        // Если контент не является строкой или DOM-элементом, удаляем всплывающее окно и обновляем стек
        popupElement.remove();
        updateStack();
    }

    // Навешиваем обработчики событий для закрытия всплывающего окна
    popupElement.addEventListener("click", close);
    popupCloseButton.addEventListener("click", close);
    popupWindow.addEventListener("click", function (event) {
        event.stopPropagation();
    });

    // Задержка перед добавлением класса "visible" для анимации
    setTimeout(function () {
        if (popupElement) {
            popupElement.classList.add("visible");
        }
        noScroll.hideScroll();
    }, 10);

    // Функция для закрытия всплывающего окна
    function close() {
        // Вызываем колбэк onClose, если он указан в параметрах
        if (currentPopup.options.onClose) {
            currentPopup.options.onClose();
        }
        
        // Сбрасываем текущее всплывающее окно
        currentPopup = null;

        // Удаляем всплывающее окно с анимацией и восстанавливаем прокрутку
        if (popupElement) {
            popupElement.addEventListener("transitionend", function(event) {
                if (popupElement) {
                    // Если указан параметр keepContentOnClose, восстанавливаем контент в исходное положение
                    if (options.keepContentOnClose) {
                        document.body.appendChild(content);
                    }

                    // Удаляем всплывающее окно из DOM
                    popupElement.remove();
                    popupElement = null;
                    noScroll.showScroll();
                }
            });

            // Убираем класс "visible" для запуска анимации закрытия
            popupElement.classList.remove("visible");
        }
        
        // Обновляем стек отображаемых всплывающих окон
        updateStack();
    }

    // Добавляем функцию закрытия всплывающего окна в объект управления
    currentPopup.control.close = close;

    // Добавляем всплывающее окно в DOM
    document.body.appendChild(popupElement);
}
